import React from "react";
import "./listUsersTable.scss";
import {
  Box,
  Chip,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  cross_default_icon,
  cross_disabled_icon,
  down_sort_arrow_icon,
  plus_dashed_active_icon,
  plus_dashed_default_icon,
  switch_user_icon,
  up_sort_arrow_icon,
} from "../../constants/icons";
import IconButtons from "../IconButtons/IconButtons";
import TablePagination from "../TablePagination/TablePagination";
import {
  ROLE_TYPES,
  filterRolesDropDown,
  font_15,
  font_21,
  font_9,
  getAbbreviation,
  getImpersonateUserEndpoint,
} from "../../utils/utils";
import { authCookieName, env } from "../../config/config";
import Constants, {
  RolesList,
  HelpTexts,
  sortInAscending,
  sortInDescending,
} from "../../constants/constants";
import { useSelector } from "react-redux";
import UserTableAvatar from "../UserTableAvatar/UserTableAvatar";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";
const { USER } = RolesList;
const { SELF_REMOVAL_OF_ADMIN_ROLE, ALL_ROLES_ASSIGNED } = HelpTexts;
const onImpersonateLoginClick = (email) =>
  (window.location.href = getImpersonateUserEndpoint(email));

// Helper function to get sort icon size
const getSortIconSize = (isActive) => (isActive ? null : font_9);

// Component for Sortable Column Header
const SortableColumnHeader = ({
  label,
  sortKey,
  sortBy,
  orderBy,
  handleSorting,
  minWidth,
  align = "left",
  showDescIcon = true,
}) => {
  const isAscActive = sortBy === sortKey && orderBy === "ASC";
  const isDescActive = sortBy === sortKey && orderBy === "DESC";
  const shouldShowDescIcon =
    showDescIcon || sortBy !== sortKey || orderBy !== "DESC";

  return (
    <TableCell align={align} sx={{ minWidth }} className="padding-left-1">
      <Typography variant="font_13_bold" className="flex-table-sort-head">
        <Box className="table-sort-head">{label}</Box>
        <Box className="table-sort-icon">
          <IconButtons
            height={getSortIconSize(isAscActive)}
            width={getSortIconSize(isAscActive)}
            image={up_sort_arrow_icon}
            handleClick={() => handleSorting("ASC", sortKey)}
            disabled={isAscActive}
            tooltip={sortInAscending}
          />
          {shouldShowDescIcon && (
            <IconButtons
              height={getSortIconSize(isDescActive)}
              width={getSortIconSize(isDescActive)}
              image={down_sort_arrow_icon}
              handleClick={() => handleSorting("DESC", sortKey)}
              disabled={isDescActive}
              tooltip={sortInDescending}
            />
          )}
        </Box>
      </Typography>
    </TableCell>
  );
};

// Component for Roles Chips
const RolesChips = ({
  roles,
  isAdmin,
  security,
  userId,
  handleDeleteModalOpen,
  fullName,
}) => {
  return (
    <Box className="user-roles-container">
      <Box className="roles-chip-container">
        {roles?.results?.map((role, index) => {
          const disableTooltip =
            ROLE_TYPES?.ADMIN?.check([role]) &&
            isAdmin &&
            security?.loggedInUser?.id === userId;
          if (role === USER) return null;
          return (
            <Chip
              key={index}
              label={ROLE_TYPES.getLabel(role)}
              variant="outlined"
              size="small"
              color="secondary"
              className="user-roles-chip"
              deleteIcon={
                <IconButtons
                  height={0.5}
                  width={0.5}
                  image={cross_default_icon}
                  handleClick={() =>
                    handleDeleteModalOpen(userId, role, fullName)
                  }
                  disabled={disableTooltip}
                  disabledImage={cross_disabled_icon}
                  disabledTooltip={SELF_REMOVAL_OF_ADMIN_ROLE}
                  classList="user-role-cross"
                  tooltip="Remove Role"
                />
              }
              onDelete={isAdmin}
            />
          );
        })}
      </Box>
    </Box>
  );
};

// Component for Action Buttons
const ActionButtons = ({
  isAdmin,
  isSpoc,
  authCookieName,
  env,
  security,
  userId,
  email,
  fullName,
  id,
  roles,
  competency,
  filterRolesList,
  handleUserAddModalOpen,
}) => {
  const isImpersonateMode =
    authCookieName === Constants.IMPERSONATECOOKIEKEY(env);
  const canShowSwitchUser =
    (isAdmin || isSpoc) &&
    !isImpersonateMode &&
    id !== security?.loggedInUser?.id;
  const actionBoxClassName = isImpersonateMode
    ? "flex-center"
    : "table-action-icons";
  const switchUserLinkClassName = !isAdmin && isSpoc ? "padding-left-1" : "";

  return (
    <Box className={actionBoxClassName}>
      {isAdmin && (
        <IconButtons
          width={font_21}
          height={font_21}
          image={plus_dashed_active_icon}
          tooltip="Add Role"
          handleClick={() =>
            handleUserAddModalOpen(fullName, id, roles, competency)
          }
          disabled={!filterRolesList?.length}
          disabledImage={plus_dashed_default_icon}
          disabledTooltip={ALL_ROLES_ASSIGNED}
        />
      )}
      {canShowSwitchUser && (
        <Link
          onClick={onImpersonateLoginClick.bind(this, email)}
          className={switchUserLinkClassName}
        >
          <IconButtons
            width={font_15}
            height={font_15}
            image={switch_user_icon}
            tooltip="Switch User"
          />
        </Link>
      )}
    </Box>
  );
};

// Component for User Table Row
const UserTableRow = ({
  user,
  index,
  isAdmin,
  isSpoc,
  security,
  roleOptions,
  handleDeleteModalOpen,
  handleUserAddModalOpen,
  authCookieName,
  env,
}) => {
  const {
    id,
    profilePictureUrl,
    fullName,
    email,
    competency,
    designation,
    businessUnit,
    roles,
    mentor,
    reportingManager,
    employeeCode,
    deleted,
  } = user;

  const rolesIds = roles?.results;
  const getRolesList = filterRolesDropDown(competency, roleOptions);
  const filterRolesList = getRolesList?.filter(
    (e) => !rolesIds?.includes(e?.key)
  );

  return (
    <TableRow className="table-body-row" hover>
      <TableCell align="left" className="padding-left-1">
        <UserTableAvatar
          imgAlt={fullName}
          imgSrc={profilePictureUrl}
          href={employeeCode}
          label={fullName}
          subLabel={email}
          deleted={deleted}
        />
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <UserTableAvatar
          imgAlt={mentor?.fullName}
          imgSrc={mentor?.profilePictureUrl}
          href={mentor?.employeeCode}
          label={mentor?.fullName}
          subLabel={mentor?.designation?.name}
          deleted={mentor?.deleted}
          tooltip={mentor?.email}
        />
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <UserTableAvatar
          imgAlt={reportingManager?.fullName}
          imgSrc={reportingManager?.profilePictureUrl}
          href={reportingManager?.employeeCode}
          label={reportingManager?.fullName}
          subLabel={reportingManager?.designation?.name}
          tooltip={reportingManager?.email}
          deleted={reportingManager?.deleted}
        />
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">{competency?.name}</Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">{businessUnit?.name}</Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <BootstrapTooltip title={designation?.name}>
          <Typography variant="font_12" display="block">
            {getAbbreviation(designation?.name)}
          </Typography>
        </BootstrapTooltip>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <RolesChips
          roles={roles}
          isAdmin={isAdmin}
          security={security}
          userId={id}
          handleDeleteModalOpen={handleDeleteModalOpen}
          fullName={fullName}
        />
      </TableCell>
      {(isAdmin || isSpoc) && (
        <TableCell align="center" className="padding-left-1 padding-right-1">
          <ActionButtons
            isAdmin={isAdmin}
            isSpoc={isSpoc}
            authCookieName={authCookieName}
            env={env}
            security={security}
            userId={id}
            email={email}
            fullName={fullName}
            id={id}
            roles={roles}
            competency={competency}
            filterRolesList={filterRolesList}
            handleUserAddModalOpen={handleUserAddModalOpen}
          />
        </TableCell>
      )}
    </TableRow>
  );
};

const ListUsersTable = ({
  getUsersData,
  totalPages,
  handlePageChange,
  currentPage,
  handleUserAddModalOpen,
  handleDeleteModalOpen,
  handleSorting,
  sortBy,
  orderBy,
  roleOptions,
}) => {
  const security = useSelector((state) => state?.security);
  const roles = security?.roles;
  const isAdmin = ROLE_TYPES.isAdmin(roles);
  const isSpoc = security?.loggedInUser?.permission?.spoc;

  return (
    <>
      <TableContainer className="table-container">
        <Table stickyHeader>
          <TableHead className="table-head-container">
            <TableRow className="table-head-row" sx={{ height: "2.711rem" }}>
              <SortableColumnHeader
                label="Name & Email"
                sortKey="name"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="13rem"
                showDescIcon={sortBy !== "name" || orderBy !== "DESC"}
              />
              <SortableColumnHeader
                label="Mentor"
                sortKey="mentorName"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="13rem"
              />
              <SortableColumnHeader
                label="Reporting Manager"
                sortKey="reportingManagerName"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="13rem"
              />
              <SortableColumnHeader
                label="Competency"
                sortKey="competencyName"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="8rem"
              />
              <SortableColumnHeader
                label="Business Unit"
                sortKey="businessUnitName"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="8rem"
              />
              <SortableColumnHeader
                label="Designation"
                sortKey="designationName"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="7rem"
                align="center"
              />
              <TableCell
                align="left"
                sx={{ minWidth: "4rem" }}
                className="padding-left-1"
              >
                <Typography variant="font_13_bold">Roles</Typography>
              </TableCell>
              {(isAdmin || isSpoc) && (
                <TableCell
                  align="center"
                  sx={{ minWidth: "5rem", maxWidth: "5rem", width: "5rem" }}
                  className="padding-left-1 padding-right-1"
                >
                  <Typography variant="font_13_bold">Actions</Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {getUsersData?.data?.results?.map((user, index) => (
              <UserTableRow
                key={index}
                user={user}
                index={index}
                isAdmin={isAdmin}
                isSpoc={isSpoc}
                security={security}
                roleOptions={roleOptions}
                handleDeleteModalOpen={handleDeleteModalOpen}
                handleUserAddModalOpen={handleUserAddModalOpen}
                authCookieName={authCookieName}
                env={env}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container className="table-pagination-container">
        <Typography variant="font_16">
          Total {getUsersData?.data?.totalResults} Records
        </Typography>
        <TablePagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          currentPage={currentPage}
        />
      </Grid>
    </>
  );
};

export default ListUsersTable;
